import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useFetchFromServer } from '../../api/fetchHook';
import { ExecutionDetails } from '../../../../shared/src/data/executionDetails';
import { serverRoutes } from '../../layout/serverRoutes';
import { S3Link } from '../../../../shared/src/data/s3Link';
import { useEffect, useState } from 'react';

export const usePendingExecutions = () => {
    const { fetchJsonFromServer } = useFetchFromServer()
    const query = useQuery<ExecutionDetails[], Error>(
        ['pendingExecutions'],
        async () => {
            const response = await fetchJsonFromServer(serverRoutes.api.executions.pending());
            return response as ExecutionDetails[];
        },
        {
            refetchOnWindowFocus: false,
            staleTime: Infinity,
        }
    );

    return {
        ...query,
        refetchPendingExecutions: query.refetch,
    };
};

export const useFinishedExecutions = (lastAmount: number) => {
    const { fetchJsonFromServer } = useFetchFromServer()
    return useQuery<ExecutionDetails[], Error>(
        ['finishedExecutions', lastAmount],
        async () => {
            const response = await fetchJsonFromServer(serverRoutes.api.executions.finished(lastAmount));
            return response as ExecutionDetails[];
        },
        {
            refetchOnWindowFocus: false,
            staleTime: Infinity,
        }
    );
};

export const useCreateExecution = () => {
    const queryClient = useQueryClient();
    const { fetchFromServer } = useFetchFromServer();
    
    return useMutation<void, Error, { scanId: string; executionParamsLink: S3Link }>(
        async ({ scanId, executionParamsLink }) => {
            const response = await fetchFromServer(serverRoutes.api.executions.route(), {
                method: 'POST',
                body: JSON.stringify({ scanId, executionParamsLink }), // Send only required fields
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            if (!response.ok) {
                throw new Error('Error creating execution');
            }
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['pendingExecutions']);
                queryClient.invalidateQueries(['finishedExecutions']);
                queryClient.invalidateQueries(['queuedExecutions']);
            },
        }
    );
};

export const useRemoveExecution = () => {
    const queryClient = useQueryClient();
    const { fetchFromServer } = useFetchFromServer()
    return useMutation<void, Error, string>(
        async (executionId) => {
            const response = await fetchFromServer(serverRoutes.api.executions.execution(executionId), {
                method: 'DELETE',
            });
            if (response.status !== 200) {
                throw new Error('Error removing execution');
            }
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['pendingExecutions']);
                queryClient.invalidateQueries(['finishedExecutions']);
                queryClient.invalidateQueries(['queuedExecutions']);
            },
        }
    );
};

export function useScanPending(scanId: string): boolean {
    const { fetchFromServer } = useFetchFromServer();
    const [isPending, setIsPending] = useState<boolean>(false);
  
    useEffect(() => {
      const checkPendingStatus = async () => {
        try {
          const response = await fetchFromServer(serverRoutes.api.executions.scanPending(scanId));
          if (!response.ok) {
            throw new Error('Failed to check scan pending status');
          }
          const data = await response.json();
          setIsPending(data.pending);
        } catch (error) {
          console.error(error);
          setIsPending(false);
        }
      };
  
      if (scanId) {
        checkPendingStatus();
      }
    }, [scanId, fetchFromServer]);
  
    return isPending;
  }

  export const useStartScan = () => {
    const queryClient = useQueryClient();
    const { fetchFromServer } = useFetchFromServer();
    
    return useMutation<void, Error, { scanId: string }>(
        async ({ scanId }) => {
            const response = await fetchFromServer(serverRoutes.api.executions.startScan(scanId), {
                method: 'POST',
            });
            if (!response.ok) {
                throw new Error('Error starting execution');
            }
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['pendingExecutions']);
                queryClient.invalidateQueries(['finishedExecutions']);
                queryClient.invalidateQueries(['queuedExecutions']);
            },
        }
    );
};

export const useStopScan = () => {
    const queryClient = useQueryClient();
    const { fetchFromServer } = useFetchFromServer();
    
    return useMutation<void, Error, { scanId: string }>(
        async ({ scanId }) => {
            const response = await fetchFromServer(serverRoutes.api.executions.stopScan(scanId), {
                method: 'POST',
            });
            if (!response.ok) {
                throw new Error('Error stopping execution');
            }
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['pendingExecutions']);
                queryClient.invalidateQueries(['finishedExecutions']);
                queryClient.invalidateQueries(['queuedExecutions']);
            },
        }
    );
};

export const useStartScans = () => {
    const queryClient = useQueryClient();
    const { fetchFromServer } = useFetchFromServer();

    return useMutation<void, Error, { ids: string[] }>(
        async ({ ids }) => {
            const response = await fetchFromServer(serverRoutes.api.executions.runScans(), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ ids }),
            });

            if (!response.ok) {
                throw new Error('Error starting executions');
            }
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['pendingExecutions']);
                queryClient.invalidateQueries(['finishedExecutions']);
                queryClient.invalidateQueries(['queuedExecutions']);
            },
            onError: (error) => {
                console.error('Error starting scans:', error.message);
            },
        }
    );
};

export const useStopScans = () => {
    const queryClient = useQueryClient();
    const { fetchFromServer } = useFetchFromServer();

    return useMutation<void, Error, { ids: string[] }>(
        async ({ ids }) => {
            const response = await fetchFromServer(serverRoutes.api.executions.stopScans(), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ ids }),
            });

            if (!response.ok) {
                throw new Error('Error stopping executions');
            }
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['pendingExecutions']);
                queryClient.invalidateQueries(['finishedExecutions']);
                queryClient.invalidateQueries(['queuedExecutions']);
            },
            onError: (error) => {
                console.error('Error stopping scans:', error.message);
            },
        }
    );
};

export const useQueuedExecutions = () => {
    const { fetchJsonFromServer } = useFetchFromServer()
    return useQuery<ExecutionDetails[], Error>(
        ['queuedExecutions'],
        async () => {
            const response = await fetchJsonFromServer(serverRoutes.api.executions.queued());
            return response as ExecutionDetails[];
        },
        {
            refetchOnWindowFocus: false,
            staleTime: Infinity,
        }
    );
};

export const useExecutions = () => {
    const pendingExecutionsQuery = usePendingExecutions();
    const finishedExecutionsQuery = useFinishedExecutions(10);
    const createExecutionMutation = useCreateExecution();
    const removeExecutionMutation = useRemoveExecution();
    const startScanMutation = useStartScan();
    const stopScanMutation = useStopScan();
    const startScansMutation = useStartScans();
    const stopScansMutation = useStopScans();
    const queuedExecutionsQuery = useQueuedExecutions();

    return {
        pendingExecutions: pendingExecutionsQuery.data || [],
        isLoadingPending: pendingExecutionsQuery.isLoading,
        pendingError: pendingExecutionsQuery.error,
        refetchPendingExecutions: pendingExecutionsQuery.refetchPendingExecutions,

        finishedExecutions: finishedExecutionsQuery.data || [],
        isLoadingFinished: finishedExecutionsQuery.isLoading,
        finishedError: finishedExecutionsQuery.error,

        createExecution: createExecutionMutation.mutateAsync,
        removeExecution: removeExecutionMutation.mutateAsync,
        
        startScan: startScanMutation.mutateAsync,
        stopScan: stopScanMutation.mutateAsync,

        startScans: startScansMutation.mutateAsync,
        isLoadingStartScans: startScansMutation.isLoading,
        startScansError: startScansMutation.error,

        stopScans: stopScansMutation.mutateAsync,

        queuedExecutions: queuedExecutionsQuery.data || [],

        useScanPending,
    };
};